
/**
 * `values` admite null: una profundidad SIN sensor no es "cero", es ausencia
 * de dato. Antes llegaba como 0 y se pintaba un punto rojo con "0.0%" — para
 * una sonda Xiaoma de 1-2 puertos eso significaba 5 profundidades marcadas
 * como alarma permanente, indistinguibles de suelo realmente seco.
 * Los null se saltan en la línea y se dibujan como aro gris hueco con "—".
 */
interface Props {
  values: Array<number | null>;
  lo: number;
  hi: number;
  unit: string;
  label: string;
  actualLabel: string;
  minLabel: string;
  maxLabel: string;
  depthThresholds?: ReadonlyArray<{ lo: number; hi: number }>;
  /** Profundidades reales del sensor — ya no una lista fija global. */
  depths: number[];
}

export default function DepthProfileChart({ values, lo, hi, unit, label, actualLabel, minLabel, maxLabel, depthThresholds, depths }: Props) {
  const W = 380;
  const H = 280;
  const ml = 52; // left margin for depth labels
  const mr = 24;
  const mt = 36;
  const mb = 32;
  const cW = W - ml - mr;
  const cH = H - mt - mb;

  const present = values.filter((v): v is number => v != null);
  const allVals = [...present, lo, hi];
  const minV = Math.min(...allVals) * 0.85;
  const maxV = Math.max(...allVals) * 1.15;
  const range = maxV - minV || 1;

  const xScale = (v: number) => ml + ((v - minV) / range) * cW;
  const yScale = (i: number) => mt + (i / Math.max(1, depths.length - 1)) * cH;

  const loX = xScale(lo);
  const hiX = xScale(hi);

  // Tramos separados: unir a través de un hueco insinuaría una transición
  // medida que nadie midió.
  const segments: string[][] = [];
  values.forEach((v, i) => {
    if (v == null) { segments.push([]); return; }
    if (segments.length === 0) segments.push([]);
    segments[segments.length - 1].push(`${xScale(v)},${yScale(i)}`);
  });
  const polylines = segments.filter(seg => seg.length > 1).map(seg => seg.join(' '));

  // X axis tick values
  const ticks = 5;
  const xTicks = Array.from({ length: ticks }, (_, i) => {
    const v = minV + (i / (ticks - 1)) * range;
    return { v, x: xScale(v) };
  });

  return (
    <svg width="100%" height="auto" viewBox={`0 0 ${W} ${H}`}>
      {/* Title */}
      <text x={W / 2} y={16} fontSize="10" fill="var(--gray)" textAnchor="middle" fontWeight={600}>
        {label}
      </text>

      {/* Optimal range band */}
      <rect x={loX} y={mt} width={Math.max(0, hiX - loX)} height={cH} fill="var(--green)" fillOpacity="0.07" />

      {/* Horizontal grid lines */}
      {depths.map((_, i) => (
        <line key={i} x1={ml} y1={yScale(i)} x2={ml + cW} y2={yScale(i)} stroke="var(--border)" strokeWidth="1" />
      ))}

      {/* Vertical grid lines */}
      {xTicks.map(({ x }, i) => (
        <line key={i} x1={x} y1={mt} x2={x} y2={mt + cH} stroke="#f0f0ec" strokeWidth="1" />
      ))}

      {/* Min threshold line */}
      <line x1={loX} y1={mt} x2={loX} y2={mt + cH} stroke="var(--amber)" strokeWidth="1.5" strokeDasharray="4,3" />
      <text x={loX} y={mt - 6} fontSize="8" fill="var(--amber)" textAnchor="middle">{minLabel}</text>

      {/* Max threshold line */}
      <line x1={hiX} y1={mt} x2={hiX} y2={mt + cH} stroke="var(--red)" strokeWidth="1.5" strokeDasharray="4,3" />
      <text x={hiX} y={mt - 6} fontSize="8" fill="var(--red)" textAnchor="middle">{maxLabel}</text>

      {/* Actual line */}
      {polylines.map((pts, i) => (
        <polyline key={i} points={pts} fill="none" stroke="var(--green)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      ))}

      {/* Actual dots + labels */}
      {values.map((v, i) => {
        const y = yScale(i);
        if (v == null) {
          // Sin sensor a esta profundidad: aro hueco y guion, nunca un valor.
          return (
            <g key={i}>
              <circle cx={ml} cy={y} r={4} fill="none" stroke="var(--border)" strokeWidth="1.5" />
              <text x={ml + 8} y={y + 4} fontSize="9" fill="var(--gray)">—</text>
            </g>
          );
        }
        const x = xScale(v);
        const dlo = depthThresholds?.[i]?.lo ?? lo;
        const dhi = depthThresholds?.[i]?.hi ?? hi;
        const margin = (dhi - dlo) * 0.15;
        const status = v < dlo - margin || v > dhi + margin ? 'alert' : v < dlo || v > dhi ? 'warn' : 'good';
        const dotColor = status === 'good' ? 'var(--green)' : status === 'warn' ? 'var(--amber)' : 'var(--red)';
        return (
          <g key={i}>
            <circle cx={x} cy={y} r={5} fill={dotColor} />
            <text x={x + 8} y={y + 4} fontSize="9" fill="var(--dark)" fontWeight={600}>
              {v.toFixed(1)}{unit}
            </text>
          </g>
        );
      })}

      {/* Depth labels (Y axis) */}
      {depths.map((d, i) => (
        <text key={d} x={ml - 8} y={yScale(i) + 4} fontSize="9" fill="var(--gray)" textAnchor="end">
          {d}cm
        </text>
      ))}

      {/* X axis value labels */}
      {xTicks.map(({ v, x }, i) => (
        <text key={i} x={x} y={mt + cH + 16} fontSize="8" fill="var(--gray)" textAnchor="middle">
          {v.toFixed(0)}
        </text>
      ))}

      {/* Unit label on x axis */}
      <text x={ml + cW / 2} y={H - 4} fontSize="8" fill="var(--gray)" textAnchor="middle">{unit}</text>

      {/* Legend */}
      <g transform={`translate(${ml}, ${H - 14})`}>
        <circle cx={0} cy={0} r={4} fill="var(--green)" />
        <text x={8} y={4} fontSize="8" fill="var(--gray)">{actualLabel}</text>
        <line x1={60} y1={0} x2={76} y2={0} stroke="var(--amber)" strokeWidth="1.5" strokeDasharray="3,2" />
        <text x={80} y={4} fontSize="8" fill="var(--gray)">{minLabel}</text>
        <line x1={120} y1={0} x2={136} y2={0} stroke="var(--red)" strokeWidth="1.5" strokeDasharray="3,2" />
        <text x={140} y={4} fontSize="8" fill="var(--gray)">{maxLabel}</text>
      </g>
    </svg>
  );
}
